// Admin: live DB schema map of the users and woody_wtf databases.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import api from '../api';

const s = {
  page:    { minHeight: '100vh', background: 'var(--bg)' },
  content: { maxWidth: '1400px', margin: '0 auto', padding: '40px 32px' },
  heading: { fontSize: '22px', fontWeight: 600, letterSpacing: '-0.02em', marginBottom: '8px' },
  sub:     { fontSize: '13px', color: 'var(--text-muted)', marginBottom: '32px' },
  section: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'calc(var(--radius) * 2)', padding: '24px', marginBottom: '24px' },
  dbTitle: { fontSize: '14px', fontWeight: 600, marginBottom: '4px', color: 'var(--text)' },
  table:   { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  th:      { textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid var(--border)', color: 'var(--text-muted)', fontWeight: 500, fontFamily: "'DM Mono', monospace", fontSize: '10px', letterSpacing: '0.05em', textTransform: 'uppercase', whiteSpace: 'nowrap' },
  td:      { padding: '6px 10px', borderBottom: '1px solid var(--border)', verticalAlign: 'top' },
  btn:     { padding: '5px 11px', borderRadius: 'var(--radius)', border: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: 500, fontFamily: "'DM Sans', sans-serif" },
  err:     { color: 'var(--error)', fontSize: '13px', marginBottom: '16px' },
  summary: { cursor: 'pointer', fontSize: '13px', color: 'var(--text)', padding: '6px 0' },
};

export default function AdminDbMap() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dbs, setDbs] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (user && !user.is_admin) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  const load = useCallback(async () => {
    setDbs(null);
    setErr('');
    try {
      const r = await api.get('/admin/db-map');
      setDbs(r.data);
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    }
  }, []);

  const download = () => {
    const blob = new Blob([JSON.stringify(dbs, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `db-map-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  useEffect(() => { load(); }, [load]);

  return (
    <div style={s.page}>
      <Navbar backTo={{ label: '← Admin', href: '/admin' }} />

      <div style={s.content}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <h1 style={s.heading}>DB Map</h1>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={load} style={{ ...s.btn, background: 'var(--surface)', color: 'var(--text)' }}>Refresh</button>
            {dbs && <button onClick={download} style={{ ...s.btn, background: 'var(--surface)', color: 'var(--text)' }}>Download JSON</button>}
          </div>
        </div>
        <p style={s.sub}>Live schema of both databases, generated on each load. Row counts are planner estimates.</p>

        {err && <div style={s.err}>✕ {err}</div>}
        {!dbs && !err && <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Loading…</div>}

        {(dbs || []).map(db => (
          <div key={db.name} style={s.section}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px' }}>
              <div style={s.dbTitle}>{db.name}</div>
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{db.size}</span>
            </div>
            {db.description && <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '12px' }}>{db.description}</div>}
            <div>
              {db.tables.map(t => (
                <details key={t.schema + '.' + t.name} style={{ borderTop: '1px solid var(--border)' }}>
                  <summary style={s.summary}>
                    <span style={{ fontFamily: "'DM Mono', monospace", fontWeight: 600 }}>{t.schema}.{t.name}</span>
                    <span style={{ color: 'var(--text-muted)', marginLeft: '10px', fontSize: '11px' }}>
                      {t.kind} · ~{Number(t.approx_rows).toLocaleString()} rows
                    </span>
                    {t.description && <span style={{ color: 'var(--text-muted)', marginLeft: '10px', fontSize: '11px' }}>— {t.description}</span>}
                  </summary>
                  <table style={{ ...s.table, margin: '6px 0 12px' }}>
                    <thead>
                      <tr>
                        {['Column', 'Type', 'Null', 'Default', 'Description'].map(h => (
                          <th key={h} style={s.th}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {t.columns.map(c => (
                        <tr key={c.column_name}>
                          <td style={{ ...s.td, fontFamily: "'DM Mono', monospace", fontWeight: 600 }}>{c.column_name}</td>
                          <td style={{ ...s.td, fontFamily: "'DM Mono', monospace", fontSize: '11px', color: 'var(--text-muted)' }}>{c.data_type}</td>
                          <td style={{ ...s.td, color: 'var(--text-muted)' }}>{c.nullable ? 'yes' : 'no'}</td>
                          <td style={{ ...s.td, fontFamily: "'DM Mono', monospace", fontSize: '11px', color: 'var(--text-muted)' }}>{c.column_default ?? ''}</td>
                          <td style={{ ...s.td, color: 'var(--text-muted)' }}>{c.description || ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
